import type { InternalStatusHistoryEntryView } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchInternalStatusHistoryApi } from "../../../api/client";
import { statusHistoryKeys } from "./statusHistoryKeys";
import type { StatusHistoryEntry } from "./statusHistoryTypes";
import { toStatusHistoryEntry } from "./statusHistoryTypes";

/**
 * Shared queryOptions for a deal's internal status history.
 * @param {string} approvalId - The numeric approval id.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function statusHistoryQuery(approvalId: string) {
  return queryOptions({
    queryKey: statusHistoryKeys.list({ approvalId }),
    queryFn: async ({ signal }): Promise<StatusHistoryEntry[]> => {
      const response = await workbenchInternalStatusHistoryApi.getInternalStatusHistory(approvalId, {
        signal,
      });
      return (response.data.history ?? []).map((row: InternalStatusHistoryEntryView) =>
        toStatusHistoryEntry(row)
      );
    },
    staleTime: 30_000,
    enabled: !!approvalId,
  });
}

/**
 * Reads the internal status history for a deal.
 * @param {string} approvalId - The numeric approval id.
 * @returns {ReturnType<typeof useQuery<StatusHistoryEntry[]>>} The TanStack Query result.
 */
export function useStatusHistory(approvalId: string) {
  return useQuery(statusHistoryQuery(approvalId));
}
